import { useState } from 'react'
import { 
  detectSeparator, 
  parseCsvLine, 
  findColumnByName, 
  splitCsvLines,
  generateCsvTemplate as generateBaseCsvTemplate
} from '../../../utils/csvUtils'

/**
 * Validates SKU, Quantity, and Cost data types for ReplenishmentsNatura
 * @param {string} sku - SKU value
 * @param {string} quantity - Quantity value
 * @param {string} cost - Cost value
 * @param {number} lineNumber - Line number for error reporting
 * @returns {string[]} Array of error messages
 */
const validateDataTypes = (sku, quantity, cost, lineNumber) => {
  const errors = []
  
  // Validar SKU (deve ser string não vazia e identificador válido)
  if (typeof sku !== 'string' || !sku.trim()) {
    errors.push(`SKU está vazio ou inválido`)
  } else {
    const skuTrimmed = sku.trim()
    
    // Verificar se SKU contém apenas caracteres alfanuméricos, hífens e underscores
    if (!/^[a-zA-Z0-9_-]+$/.test(skuTrimmed)) {
      errors.push(`SKU "${skuTrimmed}" contém caracteres inválidos`)
    }
    
    // Verificar se não contém espaços
    if (skuTrimmed.includes(' ')) {
      errors.push(`SKU "${skuTrimmed}" não pode conter espaços`)
    }
  }
  
  // Validar Quantidade (deve ser número inteiro positivo)
  const quantityNum = parseInt(quantity, 10)
  if (isNaN(quantityNum)) {
    errors.push(`Quantidade "${quantity}" não é um número válido`)
  } else if (quantityNum < 0) {
    errors.push(`Quantidade "${quantity}" é negativa`)
  } else if (!Number.isInteger(quantityNum)) {
    errors.push(`Quantidade "${quantity}" deve ser um número inteiro`)
  }
  
  // Validar Custo (deve ser número float positivo)
  const costNum = parseFloat(cost)
  if (isNaN(costNum)) {
    errors.push(`Custo "${cost}" não é um número válido`)
  } else if (costNum < 0) {
    errors.push(`Custo "${cost}" é negativo`)
  }
  
  return errors
}

/**
 * Validates row completeness for SKU, Quantity, and Cost fields
 * @param {string} sku - SKU value
 * @param {string} quantity - Quantity value
 * @param {string} cost - Cost value
 * @param {number} lineNumber - Line number for error reporting
 * @returns {string[]} Array of error messages
 */
const validateRowCompleteness = (sku, quantity, cost, lineNumber) => {
  const errors = []
  const hasSku = sku && sku.trim()
  const hasQuantity = quantity && quantity.trim()
  const hasCost = cost && cost.trim()
  
  // Verificar se todos os campos obrigatórios estão preenchidos
  const missingFields = []
  if (!hasSku) missingFields.push('SKU')
  if (!hasQuantity) missingFields.push('Quantidade')
  if (!hasCost) missingFields.push('Custo')
  
  if (missingFields.length > 0) {
    if (missingFields.length === 3) {
      errors.push(`Linha completamente vazia`)
    } else {
      errors.push(`Campos obrigatórios não preenchidos: ${missingFields.join(', ')}`)
    }
  }
  
  return errors
}

/**
 * Validates for duplicate SKUs in the dataset
 * @param {Array} validData - Array of valid data items
 * @returns {Object} Object with validData (without duplicates) and duplicateErrors
 */
const validateDuplicateSKUs = (validData) => {
  const skuMap = new Map()
  const duplicateErrors = []
  const validDataWithoutDuplicates = []
  
  // First pass: group by SKU and identify duplicates
  validData.forEach(item => {
    const sku = item.sku
    if (!skuMap.has(sku)) {
      skuMap.set(sku, [])
    }
    skuMap.get(sku).push(item)
  })
  
  // Second pass: separate valid data from duplicates
  skuMap.forEach((items, sku) => {
    if (items.length > 1) {
      // Duplicate found - add all occurrences to errors
      const lineNumbers = items.map(item => item.lineNumber).join(', ')
      items.forEach(item => {
        duplicateErrors.push({
          lineNumber: item.lineNumber,
          sku: item.sku,
          quantity: item.quantity,
          cost: item.cost,
          error: `SKU duplicado (aparece nas linhas: ${lineNumbers})`
        })
      })
    } else {
      // No duplicate - keep in valid data
      validDataWithoutDuplicates.push(items[0])
    }
  })
  
  return {
    validData: validDataWithoutDuplicates,
    duplicateErrors
  }
}

/**
 * Processes CSV file specifically for ReplenishmentsNatura
 * Expects SKU, Quantidade, and Custo columns
 * @param {File} file - CSV file to process
 * @returns {Promise<Object>} Promise resolving to object with validData and errorData arrays
 */
const processCsvFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (event) => {
      try {
        const csvText = event.target.result
        const lines = splitCsvLines(csvText)
        
        if (lines.length < 2) {
          reject(new Error('❌ Arquivo inválido\n\nO arquivo CSV deve conter:\n• Uma linha de cabeçalho com as colunas SKU, Quantidade e Custo\n• Pelo menos uma linha com dados\n\n💡 Solução: Baixe o template e use-o como modelo.'))
          return
        }
        
        const separator = detectSeparator(csvText)
        const headers = parseCsvLine(lines[0], separator)
        
        // Buscar colunas exatas SKU, Quantidade e Custo (case insensitive)
        const skuColumn = findColumnByName(headers, 'SKU')
        const quantityColumn = findColumnByName(headers, 'quantidade')
        const costColumn = findColumnByName(headers, 'custo')
        
        const errors = []
        
        // Validar colunas obrigatórias
        if (!skuColumn) {
          errors.push('❌ Coluna "SKU" não encontrada')
        }
        
        if (!quantityColumn) {
          errors.push('❌ Coluna "Quantidade" não encontrada')
        }
        
        if (!costColumn) {
          errors.push('❌ Coluna "Custo" não encontrada')
        }
        
        if (errors.length > 0) {
          const foundColumns = headers.length > 0 ? headers.join(', ') : 'Nenhuma coluna encontrada'
          
          const errorMessage = `⚠️ Colunas obrigatórias não encontradas no arquivo\n\n` +
                              `🔍 Colunas encontradas no arquivo: ${foundColumns}\n\n` +
                              `❌ Problemas identificados:\n` +
                              errors.map(error => `• ${error.replace('❌ ', '')}`).join('\n') + 
                              `\n\n📋 Instruções para correção:\n` +
                              `• Renomeie a primeira coluna para "SKU"\n` +
                              `• Renomeie a segunda coluna para "Quantidade"\n` +
                              `• Renomeie a terceira coluna para "Custo"\n` +
                              `• As colunas podem estar em maiúsculas ou minúsculas\n` +
                              `• Certifique-se de que o arquivo tem exatamente essas três colunas\n\n` +
                              `💡 Dica: Baixe o template disponível na página para ver o formato correto`
          
          reject(new Error(errorMessage))
          return
        }
        
        const skuIndex = headers.indexOf(skuColumn)
        const quantityIndex = headers.indexOf(quantityColumn)
        const costIndex = headers.indexOf(costColumn)
        const validDataBeforeDuplicateCheck = []
        const errorData = []
        
        // Processar dados linha por linha
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim()
          if (!line) continue
          
          const values = parseCsvLine(line, separator)
          const lineNumber = i + 1
          
          // Verificar se a linha tem colunas suficientes
          if (values.length < Math.max(skuIndex + 1, quantityIndex + 1, costIndex + 1)) {
            errorData.push({
              lineNumber,
              sku: values[skuIndex] || '',
              quantity: values[quantityIndex] || '',
              cost: values[costIndex] || '',
              error: 'Número insuficiente de colunas'
            })
            continue
          }
          
          const sku = String(values[skuIndex] || '').trim()
          const quantity = String(values[quantityIndex] || '').trim()
          const cost = String(values[costIndex] || '').trim()
          
          // Validar completude da linha
          const completenessErrors = validateRowCompleteness(sku, quantity, cost, lineNumber)
          if (completenessErrors.length > 0) {
            errorData.push({
              lineNumber,
              sku,
              quantity,
              cost,
              error: completenessErrors.join('; ')
            })
            continue
          }
          
          // Se chegou até aqui, todos os campos estão preenchidos
          // Validar tipos de dados
          const dataTypeErrors = validateDataTypes(sku, quantity, cost, lineNumber)
          if (dataTypeErrors.length > 0) {
            errorData.push({
              lineNumber,
              sku,
              quantity,
              cost,
              error: dataTypeErrors.join('; ')
            })
            continue
          }
          
          // Se passou em todas as validações, adicionar aos resultados válidos temporários
          const quantityNum = parseInt(quantity, 10)
          const costNum = parseFloat(cost)
          validDataBeforeDuplicateCheck.push({ sku, quantity: quantityNum, cost: costNum, lineNumber })
        }
        
        // Validar SKUs duplicados
        const { validData, duplicateErrors } = validateDuplicateSKUs(validDataBeforeDuplicateCheck)
        
        // Adicionar erros de duplicação aos erros gerais
        errorData.push(...duplicateErrors)
        
        // Se não há dados válidos nem dados com erro, é um arquivo vazio
        if (validData.length === 0 && errorData.length === 0) {
          reject(new Error('❌ Arquivo sem dados para processar\n\n' +
                         '🔍 O arquivo contém apenas o cabeçalho, mas nenhuma linha de dados foi encontrada.\n\n' +
                         '📋 Instruções para correção:\n' +
                         '• Adicione pelo menos uma linha com dados após o cabeçalho\n' +
                         '• Certifique-se de que as linhas de dados não estão vazias\n' +
                         '• Verifique se o formato está correto: SKU,Quantidade,Custo\n\n' +
                         '💡 Dica: Use o template disponível para ver exemplos de dados válidos'))
          return
        }
        
        resolve({ validData, errorData })
        
      } catch (error) {
        reject(new Error(`❌ Erro ao processar arquivo: ${error.message}\n\n💡 Solução: Verifique se o arquivo é um CSV válido e tente novamente.`))
      }
    }
    
    reader.onerror = () => {
      reject(new Error('❌ Erro ao ler arquivo\n\n💡 Solução: Verifique se o arquivo não está corrompido e tente novamente.'))
    }
    
    reader.readAsText(file)
  })
}

/**
 * Generates and downloads a CSV template for ReplenishmentsNatura
 * @param {string} [filename='template-replenishment.csv'] - The filename for the download
 */
const generateCsvTemplate = (_event, filename = 'recebimento-natura-template.csv') => {
  const headers = ['SKU', 'Quantidade', 'Custo']
  const sampleData = [
    ['PROD001', '10', '15.50'],
    ['PROD002', '25', '8.75'], 
    ['PROD003', '5', '22.00']
  ]
  
  generateBaseCsvTemplate(filename, headers, sampleData)
}

export const useCsvProcessing = () => {
  const [csvData, setCsvData] = useState([])
  const [csvErrors, setCsvErrors] = useState([])
  const [csvError, setCsvError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)

  const handleFileUpload = async (files) => {
    if (files.length === 0) return
    
    const file = files[0]
    setIsProcessing(true)
    setCsvError('')
    setCsvErrors([])
    setShowErrorModal(false)
    
    try {
      const { validData, errorData } = await processCsvFile(file)
      setCsvData(validData)
      setCsvErrors(errorData)
      
      // Modal só deve aparecer quando NÃO há dados válidos
      // Isso cobre os casos especiais: arquivo vazio, headers faltando, etc.
      if (validData.length === 0) {
        if (errorData.length > 0) {
          const errorMessage = '⚠️ Todos os dados contêm erros:\n\n' + 
                              errorData.map(item => `Linha ${item.lineNumber}: ${item.error}`).join('\n') + 
                              '\n\n💡 Dica: Corrija os erros no arquivo CSV e tente novamente.'
          setCsvError(errorMessage)
          setShowErrorModal(true)
        }
      }
      // Se há dados válidos, mesmo que também existam erros, não mostrar modal
      // Os erros serão exibidos na tabela
    } catch (error) {
      // Este catch captura os casos especiais: headers faltando, arquivo só com header, etc.
      setCsvError(error.message)
      setCsvData([])
      setCsvErrors([])
      setShowErrorModal(true)
    } finally {
      setIsProcessing(false)
    }
  }

  const clearData = () => {
    setCsvData([])
    setCsvErrors([])
    setCsvError('')
    setShowErrorModal(false)
  }

  const closeErrorModal = () => {
    setShowErrorModal(false)
  }

  const removeErrorBySku = (sku) => {
    // Remove erro da lista de erros
    const updatedErrors = csvErrors.filter(error => error.sku !== sku)
    setCsvErrors(updatedErrors)
    
    // Encontrar o erro removido para adicionar de volta aos dados válidos
    const removedError = csvErrors.find(error => error.sku === sku)
    if (removedError) {
      const newValidItem = {
        sku: removedError.sku,
        quantity: parseInt(removedError.quantity, 10),
        cost: parseFloat(removedError.cost) || 0, // Garantir que o custo seja preservado como número
        lineNumber: removedError.lineNumber
      }
      setCsvData(prevData => [...prevData, newValidItem])
    }
  }

  const getTotalQuantity = () => {
    return csvData.reduce((sum, item) => sum + item.quantity, 0)
  }

  const getTotalCost = () => {
    return csvData.reduce((sum, item) => sum + item.cost, 0)
  }

  const hasValidData = csvData.length > 0
  const hasErrors = csvErrors.length > 0
  const isDataValid = hasValidData && !hasErrors

  return {
    csvData,
    csvErrors,
    csvError,
    isProcessing,
    showErrorModal,
    handleFileUpload,
    removeErrorBySku,
    clearData,
    closeErrorModal,
    getTotalQuantity,
    getTotalCost,
    hasData: hasValidData,
    hasValidData,
    hasErrors,
    isDataValid,
    generateCsvTemplate,
    setCsvData,
    setCsvErrors,
    setCsvError,
    setShowErrorModal
  }
}